import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';

import { PUBLICATION_PROFILE_FIELD_OPTIONS } from '../../model/options';
import type {
  EffectivePublicationProfile,
  PublicationProfileFieldKind,
  PublicationStockScope,
} from '../../model/publicationTypes';

interface PublicationProfileOverrideDialogProps {
  open: boolean;
  catalogTypeCode: string;
  current: EffectivePublicationProfile | undefined;
  saving: boolean;
  onClose: () => void;
  onSubmit: (value: {
    fields: PublicationProfileFieldKind[];
    minPhotoCount: number | null;
    requireActivePrice: boolean;
    requireStock: boolean;
    minAvailableQty: number | null;
    stockScope: PublicationStockScope;
    stockLocationId: string | null;
    allowCheckAvailability: boolean;
    allowBackorder: boolean;
  }) => void;
}

/**
 * Tenant override editor for a catalog type's publication profile. `fields` is a subset of
 * `{code, name}` and `minPhotoCount` a non-negative integer. Price and stock readiness are configured
 * as explicit gates, not arbitrary entries in the required-field dictionary.
 */
export function PublicationProfileOverrideDialog({
  open,
  catalogTypeCode,
  current,
  saving,
  onClose,
  onSubmit,
}: PublicationProfileOverrideDialogProps) {
  const { t } = useTranslation('catalog');
  const [fields, setFields] = useState<PublicationProfileFieldKind[]>([]);
  const [minPhotoCount, setMinPhotoCount] = useState('');
  const [requireActivePrice, setRequireActivePrice] = useState(false);
  const [requireStock, setRequireStock] = useState(false);
  const [minAvailableQty, setMinAvailableQty] = useState('');
  const [stockScope, setStockScope] = useState<PublicationStockScope>('AGGREGATE');
  const [stockLocationId, setStockLocationId] = useState('');
  const [allowCheckAvailability, setAllowCheckAvailability] = useState(false);
  const [allowBackorder, setAllowBackorder] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFields(current?.fields ?? []);
    setMinPhotoCount(current?.minPhotoCount != null ? String(current.minPhotoCount) : '');
    setRequireActivePrice(current?.requireActivePrice ?? false);
    setRequireStock(current?.requireStock ?? false);
    setMinAvailableQty(current?.minAvailableQty != null ? String(current.minAvailableQty) : '');
    setStockScope(current?.stockScope ?? 'AGGREGATE');
    setStockLocationId(current?.stockLocationId ?? '');
    setAllowCheckAvailability(current?.allowCheckAvailability ?? false);
    setAllowBackorder(current?.allowBackorder ?? false);
  }, [open, current]);

  const toggleField = (field: PublicationProfileFieldKind) => {
    setFields((prev) => (prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field]));
  };

  const parsedMinPhotoCount = minPhotoCount.trim() === '' ? null : Number(minPhotoCount);
  const parsedMinAvailableQty = minAvailableQty.trim() === '' ? null : Number(minAvailableQty);
  const valid =
    (parsedMinPhotoCount === null || (Number.isInteger(parsedMinPhotoCount) && parsedMinPhotoCount >= 0)) &&
    (!requireStock || parsedMinAvailableQty === null || parsedMinAvailableQty >= 0) &&
    (!requireStock || stockScope !== 'LOCATION' || stockLocationId.trim().length > 0);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        {t('publicationCatalogTypesPanel.overrideDialog.title', { code: catalogTypeCode })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <DialogContentText>{t('publicationCatalogTypesPanel.overrideDialog.description')}</DialogContentText>
          <FormGroup>
            {PUBLICATION_PROFILE_FIELD_OPTIONS.map((option) => (
              <FormControlLabel
                key={option.value}
                control={
                  <Checkbox
                    checked={fields.includes(option.value)}
                    onChange={() => toggleField(option.value)}
                  />
                }
                label={t(option.label)}
              />
            ))}
          </FormGroup>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={requireActivePrice}
                  onChange={(event) => setRequireActivePrice(event.target.checked)}
                />
              }
              label={t('publicationCatalogTypesPanel.overrideDialog.requireActivePriceLabel')}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={requireStock}
                  onChange={(event) => setRequireStock(event.target.checked)}
                />
              }
              label={t('publicationCatalogTypesPanel.overrideDialog.requireStockLabel')}
            />
          </FormGroup>
          <TextField
            label={t('publicationCatalogTypesPanel.overrideDialog.minPhotoCountLabel')}
            type="number"
            value={minPhotoCount}
            onChange={(e) => setMinPhotoCount(e.target.value)}
            error={!valid}
            helperText={
              valid ? t('publicationCatalogTypesPanel.overrideDialog.minPhotoCountHelper') : undefined
            }
            fullWidth
            slotProps={{ htmlInput: { min: 0, step: 1 } }}
          />
          {requireStock ? (
            <Stack spacing={2}>
              <TextField
                label={t('publicationCatalogTypesPanel.overrideDialog.minAvailableQtyLabel')}
                type="number"
                value={minAvailableQty}
                onChange={(e) => setMinAvailableQty(e.target.value)}
                error={parsedMinAvailableQty !== null && parsedMinAvailableQty < 0}
                helperText={t('publicationCatalogTypesPanel.overrideDialog.minAvailableQtyHelper')}
                fullWidth
                slotProps={{ htmlInput: { min: 0, step: 1 } }}
              />
              <TextField
                select
                label={t('publicationCatalogTypesPanel.overrideDialog.stockScopeLabel')}
                value={stockScope}
                onChange={(e) => setStockScope(e.target.value as PublicationStockScope)}
                fullWidth
              >
                <MenuItem value="AGGREGATE">
                  {t('publicationCatalogTypesPanel.overrideDialog.stockScopeAggregate')}
                </MenuItem>
                <MenuItem value="LOCATION">
                  {t('publicationCatalogTypesPanel.overrideDialog.stockScopeLocation')}
                </MenuItem>
              </TextField>
              {stockScope === 'LOCATION' ? (
                <TextField
                  label={t('publicationCatalogTypesPanel.overrideDialog.stockLocationIdLabel')}
                  value={stockLocationId}
                  onChange={(e) => setStockLocationId(e.target.value)}
                  error={stockLocationId.trim().length === 0}
                  fullWidth
                />
              ) : null}
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allowCheckAvailability}
                      onChange={(event) => setAllowCheckAvailability(event.target.checked)}
                    />
                  }
                  label={t('publicationCatalogTypesPanel.overrideDialog.allowCheckAvailabilityLabel')}
                />
                <FormControlLabel
                  control={
                    <Checkbox checked={allowBackorder} onChange={(event) => setAllowBackorder(event.target.checked)} />
                  }
                  label={t('publicationCatalogTypesPanel.overrideDialog.allowBackorderLabel')}
                />
              </FormGroup>
            </Stack>
          ) : null}
          <Alert severity="info" variant="outlined">
            {t('publicationCatalogTypesPanel.overrideDialog.validationNotice')}
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('common:action.cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={!valid || saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          onClick={() =>
            onSubmit({
              fields,
              minPhotoCount: parsedMinPhotoCount,
              requireActivePrice,
              requireStock,
              minAvailableQty: requireStock ? parsedMinAvailableQty : null,
              stockScope,
              stockLocationId: requireStock && stockScope === 'LOCATION' ? stockLocationId.trim() : null,
              allowCheckAvailability,
              allowBackorder,
            })
          }
        >
          {t('common:action.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
